import type { MeResponse } from "@family/shared";
import { Logo } from "../components/Logo";
import { useAuth } from "../auth/AuthProvider";
import { getFamilyConfigForHost } from "../lib/families";

/**
 * Shown to someone who is signed in but holds no membership in the family
 * whose site they are on.
 *
 * Before this existed they fell through to the normal app shell, where every
 * permission check resolved false — an empty home page, a menu with almost
 * nothing in it, and no explanation. That was most visible for people joining
 * a second family, who are already members elsewhere and so never saw the
 * onboarding flow either.
 *
 * The API has already recorded the sign-up (family_signups) and pinged this
 * family's moderators by the time this renders; there is nothing for the
 * visitor to submit, only to wait.
 */
export function AwaitingApprovalPage({ me }: { me: MeResponse | undefined }) {
  const { signOut } = useAuth();
  const familyName = getFamilyConfigForHost()?.name ?? "this family";
  const currentSlug = getFamilyConfigForHost()?.slug;

  // Families they are already in. For a second-family sign-up this is the
  // useful escape hatch: they landed here from a site that does work for them.
  const elsewhere = (me?.memberships ?? []).filter(
    (m) => m.familySlug !== currentSlug,
  );

  return (
    <div className="page--auth">
      <div className="page--auth__card stack">
        <div className="page--auth__logo">
          <Logo variant="on-light" size={120} />
        </div>
        <h1>Waiting for approval</h1>
        <p>
          Thanks for signing up to {familyName}. A moderator has been notified
          and will review your request — you'll be able to sign in here as soon
          as they approve you.
        </p>
        <p className="text-muted">
          Nothing more to do for now. If it's taking a while, contact whoever
          invited you to the site.
        </p>

        {elsewhere.length > 0 && (
          <p className="text-muted">
            You're already a member of{" "}
            {elsewhere.map((m, i) => (
              <span key={m.familySlug}>
                {i > 0 && i === elsewhere.length - 1 ? " and " : i > 0 ? ", " : ""}
                <strong>{m.familyName}</strong>
              </span>
            ))}
            . That membership is unaffected — {familyName} is a separate family
            and needs its own approval.
          </p>
        )}

        <button type="button" className="btn-secondary" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  );
}
